import { useCallback, useMemo, useState } from "react";

import type { DocgenResponse } from "@/api/schemas";
import { ExportDropdown } from "@/components/ui/export-dropdown";
import { CopyButton } from "@/components/ui/copy-button";
import { exportDocuments, triggerDownload } from "@/api/client";
import { officePayload, sprancaRequestValue, useOfficeStore } from "@/features/projects/useOfficeStore";
import { TooltipProvider } from "@/components/ui/tooltip";
import { DocTabs } from "./DocTabs";
import { ResultSummary } from "./ResultSummary";
import { useDocgenShortcuts } from "./useDocgenShortcuts";
import { ShortcutsCheatSheet } from "./ShortcutsCheatSheet";
import { useDocgenStore } from "./useDocgenStore";

interface DocgenViewProps {
  data: DocgenResponse;
}

/**
 * Top-level docgen experience — orchestrates ValidationBar, the
 * DocTabs (5 tabs), and the document-level toolbar (Copy all, Export
 * all). Sprint B layers ConfidencePanel and streaming on top of this
 * shell — see [docs/PROSE_GENERATION.md](../../../../docs/PROSE_GENERATION.md).
 */
export function DocgenView({ data }: DocgenViewProps) {
  const tekstualni = useMemo(
    () => data.tekstualni_dio.filter((s) => s.section_type !== "table"),
    [data.tekstualni_dio],
  );

  const mape = data.mape ?? [];

  const allSections = useMemo(
    () => [...data.izjave, ...data.naslovnice, ...tekstualni, ...data.tables, ...mape],
    [data, tekstualni, mape],
  );

  const allText = useMemo(() => {
    const groups: { label: string; list: typeof allSections }[] = [
      { label: "Izjave", list: data.izjave },
      { label: "Naslovnice", list: data.naslovnice },
      { label: "Tekstualni dio", list: tekstualni },
      { label: "Tablice", list: data.tables },
      { label: "Mape", list: mape },
    ];
    return groups
      .filter((g) => g.list.length)
      .map(
        (g) =>
          `\n${"=".repeat(60)}\n${g.label.toUpperCase()}\n${"=".repeat(60)}\n\n` +
          g.list.map((s) => `--- ${s.title} ---\n\n${s.content}\n\n`).join(""),
      )
      .join("")
      .trim();
  }, [data, tekstualni, mape, allSections]);

  async function exportAll(format: "docx" | "pdf") {
    const blob = await exportDocuments({
      format,
      sections: allSections,
      fact_sheet: data.fact_sheet,
      tab_name: "Projektna dokumentacija",
      variables: data.opci_variables,
      office: officePayload(useOfficeStore.getState().office) ?? undefined,
      spranca: sprancaRequestValue() ?? undefined,
    });
    triggerDownload(blob, `Permitko_dokumentacija.${format}`);
  }

  // Sprint B — W3.
  // ``data-section-id`` is set on every DocSectionCard so the
  // sparkline dot can scroll-to-section without router state. Falling
  // back to a no-op when the element is offscreen (e.g. the wrong tab)
  // is fine — the user can re-click after switching tabs.
  const jumpToSection = useCallback((sectionId: string) => {
    const el = document.querySelector<HTMLElement>(
      `[data-section-id="${CSS.escape(sectionId)}"]`,
    );
    if (!el) return;
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    el.classList.add("ring-2", "ring-ind-400");
    window.setTimeout(() => {
      el.classList.remove("ring-2", "ring-ind-400");
    }, 1200);
  }, []);

  // Sprint B — W6. Keyboard navigation operates on the flat section
  // list (j/k cross tab boundaries by jumping via ``data-section-id``,
  // which works because every tab pane mounts its sections under the
  // same selector). Cheat sheet visibility lives at this level so
  // the dialog stays mounted only when needed.
  const focusedId = useDocgenStore((s) => s.focusedSectionId);
  const [cheatOpen, setCheatOpen] = useState(false);
  useDocgenShortcuts({
    sections: allSections,
    onJump: jumpToSection,
    onShowCheatSheet: () => setCheatOpen(true),
  });

  return (
    <TooltipProvider delayDuration={200}>
      {/* F3 — secondary area under the Mapa 1 hero: per-document review
          and editing. Edits flow back into the store, so the next Mapa 1
          preview/download picks them up. */}
      <div className="flex flex-wrap items-end justify-between gap-3 border-t border-stone-200 pt-5">
        <div>
          <h2 className="text-[1rem] font-bold text-stone-800">
            Pregledaj i doradi pojedinačno
          </h2>
          <p className="text-[0.8rem] text-stone-500">
            Svaki dokument možeš urediti, regenerirati ili preuzeti zasebno —
            izmjene ulaze u Mapu 1 pri sljedećem preuzimanju.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <CopyButton text={allText} label="Kopiraj sve" />
          <ExportDropdown onExport={exportAll} label="Preuzmi generirane" />
        </div>
      </div>

      <div className="mt-4">
        <ResultSummary data={data} onJumpToSection={jumpToSection} />
        <DocTabs
          izjave={data.izjave}
          naslovnice={data.naslovnice}
          tekstualni={tekstualni}
          tables={data.tables}
          mape={mape}
          factSheet={data.fact_sheet}
          validation={data.validation}
          opciVariables={data.opci_variables}
          focusedSectionId={focusedId}
        />
      </div>

      <ShortcutsCheatSheet open={cheatOpen} onOpenChange={setCheatOpen} />
    </TooltipProvider>
  );
}
